import { useEffect, useState } from "react";
import Image from "next/image";
import Head from "next/head";
import Absen from "@/components/fragments/Absen";
import BottomNavigation from "../components/fragments/BottomNavigation";
import { useAuth } from "@/context/AuthContext";
import { EmployeeData } from "@/types/dashboard";
import withAuth from "@/components/layouts/withAuth";

const CameraPage = () => {
  const { user } = useAuth(); // Get user from AuthContext
  const [employeeData, setEmployeeData] = useState<EmployeeData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Redirect to login if user is not authenticated
  // useEffect(() => {
  //   if (!user) {
  //     router.push("/login");
  //   }
  // }, [user, router]);

  const [greeting, setGreeting] = useState("");

  useEffect(() => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) {
      setGreeting("Pagi!");
    } else if (hour >= 12 && hour < 15) {
      setGreeting("Siang!");
    } else if (hour >= 15 && hour < 18) {
      setGreeting("Sore!");
    } else {
      setGreeting("Malam!");
    }
  }, []);

  // Fetch employee data
  useEffect(() => {
    const fetchData = async () => {
      if (!user?.pgw_id) {
        setError("pgw_id is not found in user context");
        return;
      }
      setLoading(true);

      try {
        const response = await fetch(
          `https://tarum-tools.aurorasystem.co.id/kepegawaian-api/dashboard_absen_status.php?pgw_id=${user?.pgw_id}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        if (data && data.name) {
          setEmployeeData(data);
        } else {
          setError("Invalid response from the server");
        }
      } catch (err) {
        if (err instanceof Error) {
          setError(err.message || "Failed to fetch data");
        } else {
          setError("An unknown error occurred");
        }
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user]);

  // Show loading state while checking authentication or fetching data
  if (!user || loading) {
    return <p>Loading...</p>;
  }

  // Show error message if there's an error
  if (error) {
    return <p className="text-red-500">{error}</p>;
  }

  return (
    <div className="font-sans bg-gray-100 min-h-screen relative">
      {/* Header Section */}
      <div className="bg-primary text-white rounded-b-2xl shadow-md p-6 mb-6 h-48 sm:flex sm:flex-col sm:items-center sm:justify-center lg:h-64">
        <div className="flex items-center mb-4">
          <div className="w-12 h-12 bg-white text-red-500 rounded-full flex items-center justify-center mr-4">
            <Image src="/logo.svg" alt="Logo" width={120} height={32} />
          </div>
          <div>
            <p className="text-xs">Halo, Selamat {greeting}</p>
            <p className="text-xl font-semibold truncate overflow-hidden text-ellipsis whitespace-nowrap max-w-[65vw]">
              {employeeData?.name}
            </p>
          </div>
        </div>
        <div className="flex flex-row justify-between items-center -mt-2">
          {/* Left Side: Icon with Text */}
          <div className="flex flex-row items-center">
            <Image
              src="/ic_jabatan_dashboard.svg"
              alt="Icon"
              width={16}
              height={16}
              className="mr-2"
            />
            <p className="text-[13px]">
              {employeeData?.position} {employeeData?.department}
            </p>
          </div>

          {/* Divider */}
          <span className="mx-2 text-lg font-thin">|</span>

          {/* Right Side: Text */}
          <div className="flex flex-row items-center">
            <Image
              src="/ic_cabang_dashboard.svg"
              alt="Icon"
              width={16}
              height={16}
              className="mr-2"
            />
            <p className="text-[13px]">{employeeData?.office}</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 sm:px-6 md:px-8 py-4">
        <Head>
          <title>Camera</title>
        </Head>

        {/* Absen Component */}
        <Absen
          date={new Date().toLocaleDateString("id-ID", {
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric",
          })}
          is_absen_in={employeeData?.is_absen_in}
          is_absen_out={employeeData?.is_absen_out}
        />

        {/* Attendance History Section */}
        <h2 className="flex sm:justify-center text-sm sm:text-xl font-bold mt-4">
          Riwayat Absen Saat ini:
        </h2>

        <div className="mt-4 bg-white shadow-md rounded-lg p-4 w-full max-w-md mx-auto">
          <div className="flex items-center space-x-4 mb-4">
            <h2 className="text-sm sm:text-md font-semibold text-gray-800">
              Status Absensi:
            </h2>
            <span
              className="text-white text-xs font-medium px-2.5 py-0.5 rounded-full"
              style={{ backgroundColor: `#${employeeData?.color_status}` }}
            >
              {employeeData?.absent_status}
            </span>
          </div>
          <div className="flex space-x-4 mt-6">
            {/* Left side: Jam Masuk */}
            <div className="flex flex-row items-center w-48">
              <img
                src={employeeData?.inphoto}
                alt="Captured"
                className="rounded-lg w-16 h-16"
              />
              <div className="ml-1 sm:ml-4 flex flex-col">
                <h3 className="text-[11px] sm:text-sm font-semibold text-textGray mb-1 sm:mb-2">
                  Jam Masuk:
                </h3>
                <p className="font-bold">{employeeData?.inpresent}</p>
              </div>
            </div>

            {/* Right side: Jam Keluar */}
            <div className="flex flex-row items-center w-48">
              <img
                src={employeeData?.outphoto || "/placeholder-image.png"}
                alt="Captured"
                className="rounded-lg w-16 h-16"
              />
              <div className="ml-1 sm:ml-4 flex flex-col">
                <h3 className="text-[11px] sm:text-sm font-semibold text-textGray mb-1 sm:mb-2">
                  Jam Keluar:
                </h3>
                <p className="text-textGray">
                  {employeeData?.outpresent || "00:00:00"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <BottomNavigation />
    </div>
  );
};

export default withAuth(CameraPage);
